<template>
  <Layout>
    <!-- 面包屑 -->
    <div class="w-full bg-gray-100 py-3">
      <div class="max-w-6xl mx-auto px-4 lg:px-8">
        <div>
          <ol class="flex items-center text-sm text-gray-500">
            <li><router-link to="/" class="hover:text-primary">Home</router-link></li>
            <li class="before:content-['/'] before:mx-2 text-gray-800">Products</li>
          </ol>
        </div>
      </div>
    </div>

    <div class="w-full max-w-6xl mx-auto px-4 lg:px-8 py-6">
      <div class="flex gap-6">
        <!-- 桌面端左侧分类侧边栏 -->
        <aside class="hidden lg:block w-56 flex-shrink-0">
          <div class="sticky top-[88px] bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div class="bg-primary text-white px-4 py-3 font-bold text-sm">Product Categories</div>
            <ul>
              <li
                v-for="(cat, index) in tabList"
                :key="index"
                class="border-b border-gray-100 last:border-b-0 cursor-pointer px-4 py-3 text-sm transition-colors"
                :class="index === tabIndex ? 'bg-gray-50 text-primary-dark font-bold' : 'text-gray-700 hover:bg-gray-50'"
                @click="changeTab(index)"
              >{{ cat.typeName }}</li>
            </ul>
          </div>
        </aside>

        <!-- 主内容区 -->
        <div class="flex-1 min-w-0">
          <!-- 移动端分类筛选按钮 -->
          <div class="lg:hidden mb-4">
            <button
              class="w-full flex items-center justify-between px-4 py-3 border border-gray-200 rounded-lg text-sm min-h-[44px]"
              @click="categoryDrawerVisible = true"
            >
              <span>{{ activeCategoryName || 'All Categories' }}</span>
              <span>▼</span>
            </button>
          </div>

          <ProductList
            v-if="viewMode === 'list'"
            :products="filteredGoodsList"
            @view-detail="handleDetails"
            @view-inquiry="handleInquiry"
          />
          <ProductDetail
            v-else-if="viewMode === 'detail'"
            :product="currentProduct"
            :productDetail="currentProductDetail"
            v-model:activePhoto="activePhoto"
            @back="backToList"
            @inquiry="handleInquiry"
          />
          <InquiryForm
            v-else-if="viewMode === 'inquiry'"
            :product="currentProduct"
            :activePhoto="currentActivePhoto"
            v-model:quantity="quantity"
            v-model:formData="formData"
            @back="backToList"
            @submit="submitQuotation"
          />
        </div>
      </div>

      <!-- 移动端分类抽屉 -->
      <el-drawer v-model="categoryDrawerVisible" direction="btt" size="60%" :with-header="false">
        <div class="p-4">
          <h3 class="text-lg font-bold mb-4">Product Categories</h3>
          <div class="space-y-1">
            <div
              v-for="(cat, index) in tabList"
              :key="index"
              class="px-4 py-3 rounded-lg text-sm cursor-pointer transition-colors"
              :class="index === tabIndex ? 'bg-primary text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'"
              @click="changeTab(index); categoryDrawerVisible = false"
            >{{ cat.typeName }}</div>
          </div>
        </div>
      </el-drawer>
    </div>
  </Layout>
</template>

<script>
import Layout from "@/components/common/CommonLayout";
import ProductList from "@/components/product/ProductList";
import ProductDetail from "@/components/product/ProductDetail";
import InquiryForm from "@/components/product/InquiryForm";
import { getRequest } from "@/api/api";
import { getImageUrl } from "@/utils/image";

export default {
  name: "ProductView",
  components: {Layout, ProductList, ProductDetail, InquiryForm},
  data() {
    return {
      // 后端 product_category 返回 {id, name, sortOrder}，前端需要 {typeId, typeName}
      tabList: [],
      tabIndex: 0,
      // 后端 product 返回 {id, categoryId, name, description, imageUrl, model}，前端需要 {typeId}
      allGoodsList: [],
      viewMode: 'list', // 'list' | 'detail' | 'inquiry'
      selectedProductId: null,
      // 产品详情缓存：key = productId, value = productDetails 结构
      productDetailsMap: {},
      loading: false,
      loadError: null,
      activePhoto: 0,
      quantity: 1,
      formData: {
        name: '',
        email: '',
        phone: '',
        company: '',
        message: ''
      },
      categoryDrawerVisible: false
    }
  },
  computed: {
    filteredGoodsList() {
      const currentTypeId = this.tabList[this.tabIndex]?.typeId;
      return this.allGoodsList.filter(goods => goods.typeId === currentTypeId);
    },
    currentCategoryName() {
      return this.tabList[this.tabIndex]?.typeName || 'Product Categories';
    },
    activeCategoryName() {
      return this.tabList[this.tabIndex]?.typeName || '';
    },
    currentProduct() {
      return this.allGoodsList.find(goods => goods.id === this.selectedProductId) || null;
    },
    currentProductDetail() {
      return this.productDetailsMap[this.selectedProductId] || null;
    },
    currentActivePhoto() {
      if (!this.currentProductDetail) return '';
      const photos = [this.currentProductDetail.imageUrl1, this.currentProductDetail.imageUrl2,
        this.currentProductDetail.imageUrl3, this.currentProductDetail.imageUrl4].filter(Boolean);
      return photos[this.activePhoto] || photos[0] || '';
    },
    contentTitle() {
      if (this.viewMode === 'list') {
        return this.currentCategoryName;
      }
      return this.currentProductDetail?.name || this.currentProduct?.name || 'Product Details';
    }
  },
  async created() {
    await this.loadCategories();
    await this.loadAllProducts();
    const categoryIndex = this.$route.query.category;
    if (categoryIndex !== undefined) {
      this.tabIndex = parseInt(categoryIndex);
    }
  },
  watch: {
    '$route.query.category'(newVal) {
      if (newVal !== undefined) {
        this.tabIndex = parseInt(newVal);
        this.backToList();
      }
    }
  },
  methods: {
    getImageUrl,
    async loadCategories() {
      try {
        const resp = await getRequest("/api/categories");
        if (resp && resp.data && resp.data.data) {
          // 后端 {id, name} → 前端 {typeId, typeName}
          this.tabList = (resp.data.data || []).map(item => ({
            typeId: item.id,
            typeName: item.name
          }));
        }
      } catch (e) {
        console.error('Failed to load categories', e);
      }
    },
    async loadAllProducts() {
      try {
        // 不传 categoryId 时后端返回全部产品
        const resp = await getRequest("/api/products");
        if (resp && resp.data && resp.data.data) {
          // 后端 {categoryId, imageUrl, ...} → 前端 {typeId, imageUrl(getImageUrl 包装), ...}
          this.allGoodsList = (resp.data.data || []).map(item => ({
            ...item,
            typeId: item.categoryId,
            imageUrl: item.imageUrl ? getImageUrl(item.imageUrl) : ''
          }));
        }
      } catch (e) {
        console.error('Failed to load products', e);
      }
    },
    async loadProductDetail(productId) {
      // 已缓存则直接返回
      if (this.productDetailsMap[productId]) return;
      try {
        const resp = await getRequest(`/api/products/${productId}/detail`);
        console.log(`[ProductView] loadProductDetail(${productId}) response:`, resp);
        if (resp && resp.data && resp.data.data) {
          const d = resp.data.data;
          console.log(`[ProductView] loadProductDetail(${productId}) data:`, d);
          // 对齐前端 productDetails 结构（imageUrl 走 getImageUrl 包装）
          this.productDetailsMap[productId] = {
            name: d.name,
            title: d.title || d.name,
            introduction: d.introduction,
            imageUrl1: d.imageUrl1 ? getImageUrl(d.imageUrl1) : '',
            imageUrl2: d.imageUrl2 ? getImageUrl(d.imageUrl2) : '',
            imageUrl3: d.imageUrl3 ? getImageUrl(d.imageUrl3) : '',
            imageUrl4: d.imageUrl4 ? getImageUrl(d.imageUrl4) : '',
            specifications: d.specifications || []
          };
          console.log(`[ProductView] loadProductDetail(${productId}) mapped result:`, this.productDetailsMap[productId]);
        } else {
          console.warn(`[ProductView] loadProductDetail(${productId}) unexpected response structure:`, resp);
          this.loadError = `Unexpected API response for product ${productId}`;
        }
      } catch (e) {
        console.error(`[ProductView] Failed to load product detail for id=${productId}`, e);
        this.loadError = `Network error loading product ${productId}: ${e.message}`;
      }
    },
    changeTab(index) {
      this.tabIndex = index;
      this.backToList();
    },
    async handleDetails(productId) {
      this.selectedProductId = productId;
      this.activePhoto = 0;
      this.loading = true;
      this.loadError = null;
      await this.loadProductDetail(productId);
      this.loading = false;
      if (this.productDetailsMap[productId]) {
        this.viewMode = 'detail';
      } else {
        this.loadError = 'Failed to load product details. Please try again later.';
      }
    },
    async handleInquiry(productId) {
      this.selectedProductId = productId;
      this.activePhoto = 0;
      this.quantity = 1;
      this.loading = true;
      this.loadError = null;
      await this.loadProductDetail(productId);
      this.loading = false;
      if (this.productDetailsMap[productId]) {
        this.viewMode = 'inquiry';
      } else {
        this.loadError = 'Failed to load product details. Please try again later.';
      }
    },
    backToList() {
      this.viewMode = 'list';
      this.selectedProductId = null;
      this.activePhoto = 0;
      this.quantity = 1;
      this.loading = false;
      this.loadError = null;
    },
    submitQuotation(payload) {
      console.log('Submit quotation', payload);
    }
  }
}
</script>

